import { useTranslation } from 'react-i18next';
import { Sun, Moon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ThemeSelectorProps {
  value: 'light_professional' | 'dark_legacy';
  onChange: (theme: 'light_professional' | 'dark_legacy') => void;
  disabled?: boolean;
}

export const ThemeSelector = ({ value, onChange, disabled = false }: ThemeSelectorProps) => {
  const { t } = useTranslation();

  const themes = [
    {
      id: 'light_professional' as const,
      icon: Sun,
      title: t('preferences.premium.theme.light.title'),
      description: t('preferences.premium.theme.light.description')
    },
    {
      id: 'dark_legacy' as const,
      icon: Moon,
      title: t('preferences.premium.theme.dark.title'),
      description: t('preferences.premium.theme.dark.description')
    }
  ];

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-semibold text-foreground">
          {t('preferences.premium.theme.title')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('preferences.premium.theme.subtitle')}
        </p>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {themes.map((theme) => {
          const Icon = theme.icon;
          const isSelected = value === theme.id;
          
          return (
            <button
              key={theme.id}
              onClick={() => !disabled && onChange(theme.id)}
              disabled={disabled}
              className={cn(
                "relative p-6 rounded-lg border-2 transition-all duration-200",
                "hover:scale-[1.02] active:scale-[0.98]",
                "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                isSelected 
                  ? "border-primary bg-primary/5 shadow-md" 
                  : "border-border bg-card hover:border-primary/50 hover:shadow-sm",
                disabled && "opacity-50 cursor-not-allowed hover:scale-100"
              )}
              aria-pressed={isSelected}
              aria-label={theme.title}
            >
              <div className="flex flex-col items-center text-center space-y-3">
                <Icon 
                  className={cn(
                    "h-10 w-10 transition-colors",
                    isSelected ? "text-primary" : "text-muted-foreground"
                  )} 
                />
                <div className="space-y-1">
                  <h4 className={cn(
                    "font-semibold transition-colors",
                    isSelected ? "text-primary" : "text-foreground"
                  )}>
                    {theme.title}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {theme.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};